// FILE: components/Alert.tsx
// This component displays a contextual feedback message.

import React, { useState } from 'react';
import { Icon } from './icons';

export type AlertVariant = 'success' | 'warning' | 'error' | 'info';

export interface AlertProps {
  variant?: AlertVariant;
  title: string;
  description?: string; // NEW: Add description prop for better API consistency
  children?: React.ReactNode; // Make children optional since we now have description
  isDismissible?: boolean;
  className?: string;
}

export const Alert: React.FC<AlertProps> = ({ 
  variant = 'info', 
  title, 
  description, 
  children, 
  isDismissible = false, 
  className = '' 
}) => {
    const [isOpen, setIsOpen] = useState(true);
    if (!isOpen) return null;
    
    // Use description prop if provided, otherwise fall back to children
    const content = description || children;
    
    const variantConfig = {
        info: { 
            icon: 'info', 
            containerClasses: 'bg-blue-100 border border-blue-200 dark:bg-blue-900/40 dark:border-blue-800/40',
            textClasses: '!text-blue-800 dark:!text-blue-200',
            titleClasses: '!text-blue-800 dark:!text-blue-200 !font-bold'
        },
        success: { 
            icon: 'check_circle', 
            containerClasses: 'bg-green-100 border border-green-200 dark:bg-green-900/40 dark:border-green-800/40',
            textClasses: '!text-green-800 dark:!text-green-200',
            titleClasses: '!text-green-800 dark:!text-green-200 !font-bold'
        },
        warning: { 
            icon: 'warning', 
            containerClasses: 'bg-amber-100 border border-amber-200 dark:bg-amber-900/40 dark:border-amber-800/40',
            textClasses: '!text-amber-800 dark:!text-amber-200',
            titleClasses: '!text-amber-800 dark:!text-amber-200 !font-bold'
        },
        error: { 
            icon: 'error', 
            containerClasses: 'bg-red-100 border border-red-200 dark:bg-red-900/40 dark:border-red-800/40',
            textClasses: '!text-red-800 dark:!text-red-200',
            titleClasses: '!text-red-800 dark:!text-red-200 !font-bold'
        },
    };
    const styles = variantConfig[variant!];
    return (
        <div className={`w-full p-4 rounded-lg flex items-start gap-4 ${styles.containerClasses} ${className}`} role="alert">
            <Icon className={`!text-2xl mt-0.5 shrink-0 ${styles.textClasses}`}>{styles.icon}</Icon>
            <div className={`flex-grow ${styles.textClasses}`}>
                <h4 className={styles.titleClasses}>{title}</h4>
                {content && <div className="text-sm mt-1">{content}</div>}
            </div>
            {isDismissible && (
                <button onClick={() => setIsOpen(false)} aria-label="Dismiss alert" className={`p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10 ${styles.textClasses}`}>
                    <Icon className="!text-xl">close</Icon>
                </button>
            )}
        </div>
    );
};
